import React, { useEffect, useRef, useState } from 'react'
import { CameraIcon, MapPinIcon, UploadIcon } from 'lucide-react'
import {
  fetchJobAttachments,
  uploadJobFile,
  type JobAttachmentWithDetails,
  type JobCaptureSource,
} from '../../api/jobWorkflow'
import { enqueueJobPhoto, listQueuedPhotosForJob } from '../../lib/offlinePhotoQueue'
import { isLikelyOfflineError, queuedPhotoToAttachment } from '../../lib/offlinePhotoSync'
import { useAppSettings } from '../../hooks/useAppSettings'
import { usePermissions } from '../../hooks/usePermissions'
import { AttachmentDetailModal } from './AttachmentDetailModal'

interface JobGalleryProps {
  jobId: string
  /** Override policy for this instance (defaults to app setting). */
  forceCameraOnly?: boolean
  /** Increment to open the camera capture picker after the gallery mounts. */
  autoCaptureToken?: number
}

export function JobGallery({ jobId, forceCameraOnly, autoCaptureToken }: JobGalleryProps) {
  const { can } = usePermissions()
  const canAttachFiles = can('jobs.edit')
  const [attachments, setAttachments] = useState<JobAttachmentWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [selected, setSelected] = useState<JobAttachmentWithDetails | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const cameraRef = useRef<HTMLInputElement>(null)
  const { photoPolicy } = useAppSettings()
  const cameraOnly = forceCameraOnly ?? photoPolicy === 'camera_required'

  const load = async () => {
    setLoading(true)
    try {
      const [server, queued] = await Promise.all([
        fetchJobAttachments(jobId),
        listQueuedPhotosForJob(jobId),
      ])
      const queuedRows = queued.map(queuedPhotoToAttachment) as JobAttachmentWithDetails[]
      setAttachments([...queuedRows, ...server])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [jobId])

  useEffect(() => {
    if (!autoCaptureToken || loading) return
    const t = window.setTimeout(() => cameraRef.current?.click(), 120)
    return () => window.clearTimeout(t)
  }, [autoCaptureToken, loading])

  const handleUpload = async (
    files: FileList | null,
    captureSource: JobCaptureSource
  ) => {
    if (!files?.length) return
    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        const isPhoto = file.type.startsWith('image/')
        if (cameraOnly && !isPhoto) continue
        if (!navigator.onLine) {
          await enqueueJobPhoto({
            id: `pending-${jobId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            jobId,
            file,
            captureSource,
            attachmentType: isPhoto ? 'photo' : 'document',
          })
          continue
        }
        try {
          await uploadJobFile(
            jobId,
            file,
            isPhoto ? 'photo' : 'document',
            undefined,
            captureSource
          )
        } catch (e) {
          if (isLikelyOfflineError(e)) {
            await enqueueJobPhoto({
              id: `pending-${jobId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              jobId,
              file,
              captureSource,
              attachmentType: isPhoto ? 'photo' : 'document',
            })
          } else {
            throw e
          }
        }
      }
      await load()
    } catch (e) {
      alert('Upload failed: ' + (e as Error).message)
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
      if (cameraRef.current) cameraRef.current.value = ''
    }
  }

  if (loading) return <p className="text-sm text-gray-500">Loading gallery...</p>

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files, 'camera')}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => cameraRef.current?.click()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <CameraIcon className="w-4 h-4" />
          {uploading ? 'Uploading...' : 'Take Photo'}
        </button>
        {!cameraOnly && canAttachFiles && (
          <>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files, 'upload')}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <UploadIcon className="w-4 h-4" />
              Attach files
            </button>
          </>
        )}
      </div>
      {cameraOnly && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          Photo policy requires camera capture — attach from files is disabled.
        </p>
      )}
      {attachments.length === 0 ? (
        <p className="text-sm text-gray-500">No photos or attachments yet.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {attachments.map((a) => {
            const hasGps = a.latitude != null && a.longitude != null
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => setSelected(a)}
                className="text-left block border border-gray-200 rounded-lg overflow-hidden hover:shadow-md bg-white"
              >
                {a.mime_type?.startsWith('image/') ? (
                  <img
                    src={a.file_url}
                    alt={a.caption || a.file_name || ''}
                    className="w-full h-32 object-cover"
                  />
                ) : (
                  <div className="h-32 flex items-center justify-center bg-gray-100 text-sm text-gray-600 p-2">
                    {a.file_name || 'Document'}
                  </div>
                )}
                <div className="p-2 space-y-0.5">
                  <p className="text-xs text-gray-700 truncate">
                    {a.caption || a.file_name}
                  </p>
                  <p className="text-[11px] text-gray-500 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                    {a.capture_source === 'camera' ? <span>camera</span> : null}
                    {a.job_task_id || a.job_task?.label ? <span>· checklist</span> : null}
                    {hasGps ? (
                      <span className="inline-flex items-center gap-0.5 text-emerald-700">
                        <MapPinIcon className="w-3 h-3" /> GPS
                      </span>
                    ) : (
                      <span className="text-amber-700">· no GPS</span>
                    )}
                  </p>
                  {(a.captured_at || a.created_at) && (
                    <p className="text-[11px] text-gray-400 truncate">
                      {new Date(a.captured_at || a.created_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      )}

      <AttachmentDetailModal attachment={selected} onClose={() => setSelected(null)} />
    </div>
  )
}
